import { Button, Flex } from 'antd';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { fontSize, lineHeight, primitiveColors, radius, spacing, textColor } from '../../theming';
import { formatLocal } from '../../utils/vinExtraction';
import { cardStyle, colorDotStyle, labelStyle, mutedSmallStyle } from './vinExtractionStyles';
import { useNumberFormat } from './useNumberFormat';
import NoValue from './NoValue';

const SEGMENTS = [
    { key: 'withVin', color: textColor.success },
    { key: 'noVin', color: primitiveColors.gray400 },
    { key: 'inProgress', color: textColor.link },
    { key: 'pending', color: primitiveColors.gray100 },
    // SC-QA-123: pending cars without a registration or COC image; never processed, so they stay. A muted brown, not
    // orange, so orange on this card always means a warning (SC-QA-126)
    { key: 'noDocuments', color: primitiveColors.brown400 },
];
// The bar draws these from the left; pending is the track between them and the no-documents end.
const LEFT_SEGMENTS = ['withVin', 'noVin', 'inProgress'];

// The legend's numbers: once the cars without documents are counted, Pending is only the cars still to process and
// those without documents get their own item; until then Pending is every pending car and there is no such item.
const legendValues = progress => ({
    ...progress,
    pending: progress.pending - (progress.noDocuments ?? 0),
});

const doneStyle = { fontSize: fontSize.H4, lineHeight: lineHeight.H4, fontWeight: 600, color: textColor.primary };
const legendValueStyle = {
    fontSize: fontSize.sm,
    lineHeight: lineHeight.lg,
    fontWeight: 600,
    color: textColor.primary,
};
const meterStyle = {
    display: 'flex',
    height: spacing[1],
    borderRadius: radius.huge,
    overflow: 'hidden',
    backgroundColor: primitiveColors.gray100,
};
// SC-QA-126: the note under the legend: green when everything that can be processed is done, amber when new cars keep
// arriving without documents
const noteStyle = (background, border) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: spacing[1],
    fontSize: fontSize.xs,
    lineHeight: lineHeight.md,
    color: textColor.primary,
    backgroundColor: background,
    border: `1px solid ${border}`,
    borderRadius: radius.md,
    padding: `${spacing[1]} ${spacing[2]}`,
    margin: 0,
});
const NOTE_STYLES = {
    allDone: { box: noteStyle(primitiveColors.green50, primitiveColors.green100), icon: primitiveColors.green800 },
    scraper: { box: noteStyle(primitiveColors.gold50, primitiveColors.gold100), icon: primitiveColors.orange900 },
};

// the in-progress sliver is a few cars out of hundreds of thousands, so it gets a minimum width to stay visible
const segmentWidth = (value, total) => (value === 0 ? 0 : `max(${(value / total) * 100}%, 3px)`);

// "≈ 31 h" under two days, "≈ 2.5 days" from there
const useDuration = hours => {
    const { t } = useTranslation('vinExtraction');
    const { integer, oneDecimal } = useNumberFormat();
    if (hours === null) return null;
    // SC-QA-124: nothing left is exactly 0, never rounded up to "≈ 1 h"
    if (hours === 0) return t('stats.database.noneLeft');
    return hours < 48
        ? t('stats.database.hours', { value: integer(Math.max(1, Math.ceil(hours))) })
        : t('stats.database.days', { value: oneDecimal(hours / 24) });
};

const Unavailable = ({ onRetry }) => {
    const { t } = useTranslation('vinExtraction');
    return (
        <Flex align="center" gap={spacing[0.5]} wrap="wrap">
            <span style={doneStyle}>
                <NoValue label={t('stats.noValue')} />
            </span>
            <span style={mutedSmallStyle}>{t('stats.cards.pendingUnavailable')}</span>
            <Button
                type="link"
                size="small"
                onClick={onRetry}
                aria-label={t('stats.cards.retryPending')}
                style={{ padding: 0, height: 'auto', fontSize: fontSize.xxs }}
            >
                {t('retry')}
            </Button>
        </Flex>
    );
};

/**
 * SC-QA-102: how far through the vehicle database the processor is, all-time. One bar splits every car into with
 * a VIN, processed without one, in progress and pending; once counted, the pending cars without documents (never
 * processed) get their own colour at the bar's end and their own legend item (SC-QA-123).
 */
const StatDatabaseCard = ({ progress, cachedAt, hoursToFinish, pace, note, isRunning, onRetry }) => {
    const { t, i18n } = useTranslation('vinExtraction');
    const { integer, oneDecimal } = useNumberFormat();
    const timeToFinish = useDuration(hoursToFinish);
    const legend = progress && legendValues(progress);

    return (
        <Flex
            vertical
            gap={spacing[2]}
            style={cardStyle}
            role="group"
            aria-label={t('stats.database.title')}
            data-testid="vin-stat-database"
        >
            <span style={{ ...labelStyle, fontWeight: 600 }}>{t('stats.database.title')}</span>
            {!progress ? (
                <Unavailable onRetry={onRetry} />
            ) : (
                <>
                    <Flex align="baseline" gap={spacing[2]} wrap="wrap">
                        <span style={doneStyle}>
                            {progress.share === null
                                ? t('stats.database.empty')
                                : t('stats.database.done', { value: `${oneDecimal(progress.share * 100)}%` })}
                        </span>
                        <span style={mutedSmallStyle}>
                            {t('stats.database.processedOf', {
                                processed: integer(progress.processed),
                                total: integer(progress.total),
                            })}
                            {cachedAt &&
                                ` · ${t('stats.database.countedAt', { time: formatLocal(cachedAt, 'HH:mm', i18n.language) })}`}
                        </span>
                    </Flex>
                    {progress.total > 0 && (
                        <div
                            style={meterStyle}
                            role="img"
                            aria-label={t(
                                progress.noDocuments === null
                                    ? 'stats.database.barAria'
                                    : 'stats.database.barAriaNoDocuments',
                                {
                                    withVin: integer(progress.withVin),
                                    noVin: integer(progress.noVin),
                                    inProgress: integer(progress.inProgress),
                                    pending: integer(legend.pending),
                                    noDocuments: integer(progress.noDocuments ?? 0),
                                }
                            )}
                        >
                            {SEGMENTS.filter(({ key }) => LEFT_SEGMENTS.includes(key)).map(({ key, color }) => (
                                <span
                                    key={key}
                                    style={{
                                        width: segmentWidth(progress[key], progress.total),
                                        backgroundColor: color,
                                    }}
                                />
                            ))}
                            {progress.noDocuments !== null && (
                                <>
                                    <span style={{ flex: 1 }} />
                                    <span
                                        data-testid="vin-db-bar-noDocuments"
                                        style={{
                                            width: segmentWidth(progress.noDocuments, progress.total),
                                            backgroundColor: SEGMENTS[4].color,
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    )}
                    <Flex gap={`${spacing[1]} ${spacing[4]}`} wrap="wrap">
                        {SEGMENTS.filter(({ key }) => key !== 'noDocuments' || progress.noDocuments !== null).map(
                            ({ key, color }) => (
                                <Flex key={key} vertical data-testid={`vin-db-${key}`}>
                                    <Flex align="center" gap={spacing[0.5]}>
                                        <span style={colorDotStyle(color)} aria-hidden="true" />
                                        <span style={mutedSmallStyle}>{t(`stats.database.${key}`)}</span>
                                    </Flex>
                                    <Flex align="center" gap={spacing[0.5]}>
                                        <span style={legendValueStyle}>{integer(legend[key])}</span>
                                        {/* SC-QA-126: the mark sits where the cause is, on the cars without documents */}
                                        {key === 'noDocuments' && note?.kind === 'scraper' && (
                                            <WarningOutlined
                                                aria-label={t('stats.database.emptyWave.iconLabel')}
                                                style={{ color: NOTE_STYLES.scraper.icon }}
                                            />
                                        )}
                                    </Flex>
                                    {/* SC-QA-126: cars left to process are not stuck: the next hourly run takes them */}
                                    {key === 'pending' &&
                                        isRunning &&
                                        progress.noDocuments !== null &&
                                        legend.pending > 0 && (
                                            <span style={mutedSmallStyle} data-testid="vin-db-pendingHint">
                                                {t('stats.database.pendingHint')}
                                            </span>
                                        )}
                                    {/* SC-QA-108: the pending cars the processor never takes */}
                                    {key === 'noDocuments' && (
                                        <span style={mutedSmallStyle}>{t('stats.database.noDocumentsHint')}</span>
                                    )}
                                </Flex>
                            )
                        )}
                        {/* SC-QA-108: only while processing runs, at the schedule's full speed */}
                        {timeToFinish && (
                            <Flex vertical data-testid="vin-db-timeToFinish">
                                <span style={mutedSmallStyle}>{t('stats.database.timeToFinish')}</span>
                                <span style={legendValueStyle}>{timeToFinish}</span>
                                <span style={mutedSmallStyle}>
                                    {t('stats.database.atPace', {
                                        batches: integer(pace.batches),
                                        cars: integer(pace.cars),
                                    })}
                                </span>
                            </Flex>
                        )}
                    </Flex>
                    {note?.kind === 'allDone' && (
                        <div style={NOTE_STYLES.allDone.box} role="status" data-testid="vin-db-allDone">
                            <CheckCircleOutlined
                                aria-hidden="true"
                                style={{ color: NOTE_STYLES.allDone.icon, marginTop: 3 }}
                            />
                            <p style={{ margin: 0 }}>
                                <strong>{t('stats.database.allDone.title')}</strong>{' '}
                                {t('stats.database.allDone.body', { count: integer(progress.noDocuments) })}
                            </p>
                        </div>
                    )}
                    {note?.kind === 'scraper' && (
                        <div style={NOTE_STYLES.scraper.box} role="alert" data-testid="vin-db-emptyWave">
                            <WarningOutlined
                                aria-hidden="true"
                                style={{ color: NOTE_STYLES.scraper.icon, marginTop: 3 }}
                            />
                            <p style={{ margin: 0 }}>
                                <strong>
                                    {t('stats.database.emptyWave.title', {
                                        time: formatLocal(note.emptyWave.checkedAt, 'HH:mm', i18n.language),
                                    })}
                                </strong>{' '}
                                {t('stats.database.emptyWave.body', { sampled: integer(note.emptyWave.sampled) })}
                            </p>
                        </div>
                    )}
                </>
            )}
        </Flex>
    );
};

export default StatDatabaseCard;
